import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { EndeavorApi } from '../../services/ApiService';
import DocumentEndeavorApi from '../../services/api/DocumentEndeavorAPI';
import './DocumentCourseAssignModal.css';

export default function DocumentCourseAssignModal({
  open,
  document,
  endeavorMap,
  onClose,
  onSave
}) {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [endeavors, setEndeavors] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const docId = document?.documentId || document?.id;

  const loadEndeavors = useCallback(async () => {
    if (!user) return;
    setLoading(true); setError(null);
    try {
      const resp = await EndeavorApi.getEndeavorsByUser
        ? await EndeavorApi.getEndeavorsByUser(user.id || user.userId)
        : await EndeavorApi.getEndeavors(user.id || user.userId); // fallback
      let data;
      try {
        data = await resp.json();
      } catch {
        data = [];
      }
      const list = Array.isArray(data) ? data : (data.content || []);
      setEndeavors(list);
      const existingIds = new Set((document?.endeavorIds || []).map(Number));
      setSelected(existingIds);
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
  }, [user, document]);

  useEffect(() => {
    if (open) loadEndeavors();
  }, [open, loadEndeavors]);

  const toggle = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleSave = async () => {
    if (!docId) return;
    setSaving(true); setError(null);
    try {
      const current = new Set((document?.endeavorIds || []).map(Number));
      const next = selected;
      const additions = [...next].filter(id => !current.has(id));
      const removals = [...current].filter(id => !next.has(id));

      // Execute sequentially to keep it simple
      for (const addId of additions) {
        try {
          await DocumentEndeavorApi.associateDocumentWithEndeavor(docId, addId);
        } catch (e) {
          console.warn('Add association failed', e);
        }
      }
      for (const remId of removals) {
        try {
          await DocumentEndeavorApi.removeDocumentFromEndeavor(docId, remId);
        } catch (e) {
          console.warn('Remove association failed', e);
        }
      }
      onSave?.(docId, [...next]);
    } catch (e) {
      setError(e);
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return (
    <div className="course-assign-modal-backdrop" onClick={saving ? undefined : onClose}>
      <div
        className="course-assign-modal"
        role="dialog"
        aria-modal="true"
        onClick={e => e.stopPropagation()}
      >
        <div className="modal-header">
          <h3>Assign Course(s)</h3>
          <button
            className="close-btn"
            onClick={onClose}
            disabled={saving}
            aria-label="Close"
          >✕</button>
        </div>
        <div className="modal-subtitle">
          {document?.name || 'Document'} · ID {docId}
        </div>

        <div className="modal-body">
          {loading ? (
            <div className="loading-block">
              <div className="spinner" />
              <p>Loading courses...</p>
            </div>
          ) : error ? (
            <div className="error-block">
              <p>Failed to load courses.</p>
              <button onClick={loadEndeavors} className="retry-btn">Retry</button>
            </div>
          ) : endeavors.length === 0 ? (
            <div className="empty-block">
              <p>No courses found.</p>
              <p className="hint">Create a course (endeavor) first.</p>
            </div>
          ) : (
            <ul className="course-select-list">
              {endeavors.map(en => {
                const id = en.endeavorId || en.id;
                const checked = selected.has(Number(id));
                return (
                  <li key={id}>
                    <label className={`course-row ${checked ? 'selected' : ''}`}>
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggle(Number(id))}
                        disabled={saving}
                      />
                      <span className="course-name">{en.name || `Course ${id}`}</span>
                    </label>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <div className="modal-footer">
          <button
            className="btn-secondary"
            onClick={onClose}
            disabled={saving}
          >Cancel</button>
          <button
            className="btn-primary"
            onClick={handleSave}
            disabled={saving || loading}
          >
            {saving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}